import { useSectionAccess } from "../../auth/useSectionAccess";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  intakeLotDetailApi,
  type IntakeLot,
  type IntakeLotAnalysis,
  type IntakeLotDelivery,
} from "../../api/lots";
import { Registar, type Kolona } from "../../components/Registar";
import { TableSkeleton } from "../../components/Loading";
import { KPI } from "../../components/primitives.jsx";
import { formatDate, formatNumber } from "../../lib/format";
import { missingPointLabel } from "../admin/quality/archivePointLabel";

/**
 * One intake lot: what went into it, from whom, and what the lab found on it.
 *
 * Two lists, and both are paged because both grow with the work: one lot holds 502 intakes on this
 * data and up to 48 findings.
 *
 * The findings belong to the LOT and the screen says so out loud. The lab never recorded which intake
 * document a sample came from, and a lot holds 79 intakes on average over 147 days — so a finding here
 * describes the silo, not the truck. Reading it the other way is the one mistake this screen can cause.
 */
export default function IntakeLotDetailPage() {
  const { canRead: canReadArchive } = useSectionAccess("lab_archive");
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [lot, setLot] = useState<IntakeLot | null>(null);
  const [greska, setGreska] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let otkazano = false;
    setGreska(null);
    intakeLotDetailApi
      .header(id)
      .then((h) => {
        if (!otkazano) setLot(h);
      })
      .catch(() => {
        if (!otkazano) setGreska(t("lots.notFound", "Šarža nije nađena."));
      });
    return () => {
      otkazano = true;
    };
  }, [id, t]);

  const ucitajPrijeme = useCallback(
    (strana: number, velicina: number) => intakeLotDetailApi.deliveries(id!, strana, velicina),
    [id],
  );
  const ucitajNalaze = useCallback(
    (strana: number, velicina: number) => intakeLotDetailApi.analyses(id!, strana, velicina),
    [id],
  );

  const prijemi = useMemo<Kolona<IntakeLotDelivery>[]>(
    () => [
      {
        kljuc: "receivedOn",
        zaglavlje: t("lots.d.date", "Datum"),
        labela: t("lots.d.date", "Datum"),
        crtaj: (r) => (r.receivedOn ? formatDate(r.receivedOn) : "—"),
      },
      {
        kljuc: "companyName",
        zaglavlje: t("lots.d.supplier", "Kooperant"),
        labela: t("lots.d.supplier", "Kooperant"),
        crtaj: (r) => r.companyName,
      },
      {
        kljuc: "erpId",
        zaglavlje: t("lots.d.doc", "Prijemnica"),
        labela: t("lots.d.doc", "Prijemnica"),
        crtaj: (r) => r.erpId ?? "—",
      },
      {
        kljuc: "netKg",
        zaglavlje: t("lots.d.netKg", "Neto (kg)"),
        labela: t("lots.d.netKg", "Neto (kg)"),
        broj: true,
        crtaj: (r) => (r.netKg == null ? "—" : formatNumber(r.netKg)),
      },
      {
        kljuc: "vehiclePlate",
        zaglavlje: t("lots.d.plate", "Tablice"),
        labela: t("lots.d.plate", "Tablice"),
        crtaj: (r) => r.vehiclePlate ?? "—",
      },
    ],
    [t],
  );

  const nalazi = useMemo<Kolona<IntakeLotAnalysis>[]>(
    () => [
      {
        kljuc: "analysedOn",
        zaglavlje: t("lots.a.date", "Datum"),
        labela: t("lots.a.date", "Datum"),
        crtaj: (r) => (r.analysedOn ? formatDate(r.analysedOn) : "—"),
      },
      {
        kljuc: "controlPoint",
        zaglavlje: t("lots.a.point", "Kontrolna tačka"),
        labela: t("lots.a.point", "Kontrolna tačka"),
        // Without a point the reason is named: tests picked one by one, or a sampling group the lab
        // program deleted from its codebook. Neither is the same as an analysis with no group at all.
        crtaj: (r) => r.controlPoint ?? missingPointLabel(t, r) ?? "—",
      },
      {
        kljuc: "productName",
        zaglavlje: t("lots.a.product", "Uzorak"),
        labela: t("lots.a.product", "Uzorak"),
        crtaj: (r) => r.productName ?? "—",
      },
      {
        kljuc: "itemCount",
        zaglavlje: t("lots.a.tests", "Testova"),
        labela: t("lots.a.tests", "Testova"),
        broj: true,
        crtaj: (r) => formatNumber(r.itemCount),
      },
    ],
    [t],
  );

  if (greska) {
    return (
      <>
        <p className="sub">{greska}</p>
        <Link to="/procurement/lots">{t("lots.back", "Nazad na šarže")}</Link>
      </>
    );
  }

  if (!lot) return <TableSkeleton />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{lot.barcode}</h1>
          <p className="sub">
            {lot.productName ?? "—"}
            {lot.firstIntake && lot.lastIntake ? (
              <>
                {" · "}
                {lot.firstIntake === lot.lastIntake
                  ? formatDate(lot.firstIntake)
                  : `${formatDate(lot.firstIntake)} — ${formatDate(lot.lastIntake)}`}
              </>
            ) : null}
          </p>
        </div>
        <Link to="/procurement/lots" className="btn">
          {t("lots.back", "Nazad na šarže")}
        </Link>
      </div>

      <div className="kpi-grid">
        <KPI
          label={t("lots.col.intakes", "Prijema")}
          value={formatNumber(lot.intakeCount)}
          delta={undefined}
          trend={undefined}
          icon={undefined}
        />
        <KPI
          label={t("lots.col.netKg", "Neto (kg)")}
          value={formatNumber(lot.netKg)}
          delta={undefined}
          trend={undefined}
          icon={undefined}
        />
        {canReadArchive && <KPI
          label={t("lots.col.analyses", "Nalaza")}
          value={lot.analysisCount != null ? formatNumber(lot.analysisCount) : "—"}
          delta={undefined}
          trend={undefined}
          icon={undefined}
        />}
      </div>

      <Registar<IntakeLotDelivery>
        naslov={t("lots.d.title", "Prijemi u ovoj šarži")}
        ucitaj={ucitajPrijeme}
        kolone={prijemi}
        kljucReda={(r) => r.id}
        vezaNaRed={(r) => `/procurement/deliveries/${r.id}`}
        prazno={t("lots.d.empty", "Nijedan prijem nije vezan za ovu šaržu.")}
      />

      <div style={{ height: 24 }} />

      {canReadArchive && <Registar<IntakeLotAnalysis>
        naslov={t("lots.a.title", "Nalazi na šarži")}
        podnaslov={t(
          "lots.a.subtitle",
          "Nalaz se odnosi na celu šaržu, ne na pojedinačni prijem: laboratorija nije beležila sa kog je kamiona uzorak uzet.",
        )}
        ucitaj={ucitajNalaze}
        kolone={nalazi}
        kljucReda={(r) => r.id}
        prazno={t("lots.a.empty", "Na ovoj šarži nema nalaza u arhivu.")}
      />}
    </>
  );
}
